// App theme. Material UI, tuned to the 1A Auto identity (1aauto.com): a blue
// primary on white/neutral surfaces, with orange as the highlight/accent (the
// site uses orange for the search button, cart, and the rule under its header).
// makeTheme(mode) returns a light or dark theme; the rest of the app reads
// theme-aware tokens so both modes look right. Change BRAND_BLUE / ACCENT_ORANGE
// to retheme everything.

import { createTheme, alpha } from '@mui/material'

const BRAND_BLUE = '#1268C4' // 1A Auto blue (swap for the official hex if different)
const ACCENT_ORANGE = '#F47C20' // highlight accent (CTAs, header rule)

export function makeTheme(mode = 'light') {
  const dark = mode === 'dark'
  return createTheme({
    palette: {
      mode,
      primary: { main: dark ? '#4d97e0' : BRAND_BLUE, contrastText: '#ffffff' },
      secondary: { main: ACCENT_ORANGE, contrastText: '#ffffff' }, // highlight only
      background: dark
        ? { default: '#0e1116', paper: '#171b22' }
        : { default: '#f4f5f7', paper: '#ffffff' },
      text: dark
        ? { primary: '#e6e8eb', secondary: '#9aa4b2' }
        : { primary: '#1a1d23', secondary: '#5b6472' },
      divider: dark ? 'rgba(255,255,255,0.12)' : '#e4e7ec',
      success: { main: dark ? '#22c55e' : '#15803d' },
      warning: { main: dark ? '#f59e0b' : '#b45309' },
      error: { main: dark ? '#ef4444' : '#b91c1c' },
      // Custom chip colors (not a MUI standard palette channel) — added so
      // intent chips (constants/intents.js) have enough non-blue, non-orange
      // hues to work with. main/contrastText are set explicitly because,
      // unlike primary/secondary/success/warning/error, MUI does not
      // auto-augment a custom palette key with a contrastText of its own.
      teal: { main: dark ? '#2dd4bf' : '#0f766e', contrastText: dark ? '#08302c' : '#ffffff' },
      purple: { main: dark ? '#a78bfa' : '#7c3aed', contrastText: dark ? '#241b40' : '#ffffff' },
      // Message thread and reply-box colors (2026-10-08). Dark: neutral slate
      // for the customer, solid navy for us and for the draft being written
      // (so it looks like the reply it will become), one light-blue link
      // color. Light reproduces exactly what was there before: the same
      // tints, no reply-box fill, and no link override (link: null). A custom
      // palette key, so every value is explicit (MUI doesn't augment it).
      message: dark
        ? { inbound: '#232933', outbound: '#1e3a5c', replyBox: '#1e3a5c', link: '#7cb7f2' }
        : { inbound: alpha('#15803d', 0.08), outbound: alpha(BRAND_BLUE, 0.10), replyBox: 'transparent', link: null },
    },

    shape: { borderRadius: 10 },

    typography: {
      fontFamily: '"Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif',
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },

    components: {
      // Top bar echoes the 1aauto.com header: surface background + an orange
      // underline. Surface adapts to the mode; contents inherit text color.
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: dark ? '#171b22' : '#ffffff',
            color: dark ? '#e6e8eb' : '#1a1d23',
            borderBottom: `3px solid ${ACCENT_ORANGE}`,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 48 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700, backgroundColor: dark ? '#1f242c' : '#fafbfc' },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: dark ? 'rgba(77,151,224,0.16)' : 'rgba(18,104,196,0.08)',
              borderRight: `3px solid ${dark ? '#4d97e0' : BRAND_BLUE}`,
            },
            '&.Mui-selected:hover': { backgroundColor: dark ? 'rgba(77,151,224,0.24)' : 'rgba(18,104,196,0.12)' },
          },
        },
      },
    },
  })
}

export default makeTheme
